import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, XCircle, Loader, Heart, DollarSign } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';

const BACKEND_URL = '';  // Use relative URLs
const API = '/api';

const TipSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking'); // checking, success, failed
  const [paymentData, setPaymentData] = useState(null);
  const [attempts, setAttempts] = useState(0);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    } else {
      setStatus('failed');
    }
  }, [sessionId]);

  const pollPaymentStatus = async (attemptCount = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000; // 2 seconds

    if (attemptCount >= maxAttempts) {
      setStatus('failed');
      return;
    }

    try {
      setAttempts(attemptCount + 1);
      
      const response = await axios.get(`${API}/tip/status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        setStatus('success');
        setPaymentData(response.data);
        return;
      } else if (response.data.status === 'expired') {
        setStatus('failed');
        return;
      }

      // If payment is still pending, continue polling
      setTimeout(() => pollPaymentStatus(attemptCount + 1), pollInterval);
      
    } catch (error) {
      console.error('Error checking payment status:', error);
      
      // Retry on error
      if (attemptCount < maxAttempts - 1) {
        setTimeout(() => pollPaymentStatus(attemptCount + 1), pollInterval);
      } else {
        setStatus('failed');
      }
    }
  };

  const handleGoBack = () => {
    if (paymentData?.metadata?.creator_handle) {
      navigate(`/u/${paymentData.metadata.creator_handle}`);
    } else {
      navigate('/');
    }
  };

  return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center p-4">
      <Card className="bg-gray-900 border-gray-800 max-w-md w-full">
        <CardContent className="p-8 text-center">
          {status === 'checking' && (
            <>
              <Loader className="w-16 h-16 text-yellow-500 mx-auto mb-4 animate-spin" />
              <h2 className="text-2xl font-bold mb-2">Processing Payment</h2>
              <p className="text-gray-400 mb-4">
                Please wait while we confirm your tip...
              </p>
              <p className="text-sm text-gray-500">
                Attempt {attempts} of 5
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-r from-green-500 to-emerald-500 flex items-center justify-center">
                <CheckCircle className="w-12 h-12 text-white" />
              </div>
              <h2 className="text-3xl font-bold mb-2">Thank You!</h2>
              <p className="text-lg text-gray-300 mb-6">
                Your tip of <span className="text-yellow-500 font-bold">${(paymentData?.amount_total / 100).toFixed(2)}</span> has been sent successfully!
              </p>
              
              <div className="bg-purple-500/10 border border-purple-500/20 rounded-lg p-4 mb-6">
                <div className="flex items-center justify-center gap-2 text-purple-400 mb-2">
                  <Heart className="w-5 h-5" />
                  <span className="font-bold">Your support means everything!</span>
                </div>
                <p className="text-sm text-gray-400">
                  The creator has been notified and received your tip.
                </p>
              </div>

              <Button
                onClick={handleGoBack}
                className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white font-bold"
              >
                Return to Profile
              </Button>
            </>
          )}

          {status === 'failed' && (
            <>
              <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-red-500/20 flex items-center justify-center">
                <XCircle className="w-12 h-12 text-red-500" />
              </div>
              <h2 className="text-3xl font-bold mb-2">Payment Failed</h2>
              <p className="text-gray-400 mb-6">
                We couldn't process your tip. No charges were made.
              </p>
              
              <div className="space-y-3">
                <Button
                  onClick={() => navigate(-1)}
                  className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600"
                >
                  <DollarSign className="w-4 h-4 mr-2" />
                  Try Again
                </Button>
                <Button
                  onClick={() => navigate('/')}
                  variant="outline"
                  className="w-full border-gray-700"
                >
                  Go Home
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TipSuccess;
